import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import AiInsights from '../components/dashboard/AiInsights';
import ExecutiveMetrics from '../components/dashboard/ExecutiveMetrics';
import QuarterlyReportCard from '../components/dashboard/QuarterlyReportCard';
import IntegratedCalendar from '../components/dashboard/IntegratedCalendar';
import NotificationCenter from '../components/dashboard/NotificationCenter';
import StrategicActions from '../components/dashboard/StrategicActions';
import ResourceFilterMini from '../components/dashboard/ResourceFilterMini';

const Dashboard = () => {
  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <div className="pt-24 min-h-screen bg-slate-50 dark:bg-brand-dark transition-colors duration-300 pb-24">
      {/* Personalized Dashboard Header */}
      <div className="bg-brand-darker py-16 relative border-b border-brand-red/20">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage: 'linear-gradient(to right,#40E0D0 1px,transparent 1px),linear-gradient(to bottom,#40E0D0 1px,transparent 1px)',backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-brand-cyan text-xs uppercase tracking-[0.2em] font-bold block mb-2">{getTimeOfDay()}, Client</span>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4">Executive Intelligence Hub</h1>
            <div className="flex items-center gap-4 text-slate-400 text-sm font-medium">
              <span className="flex items-center gap-2">
                <SafeIcon name="Calendar" className="w-4 h-4 text-brand-yellow" />
                Next Review: Oct 15
              </span>
              <span className="w-px h-4 bg-slate-700"></span>
              <span className="flex items-center gap-2">
                <SafeIcon name="Map" className="w-4 h-4 text-brand-cyan" />
                3 Active Strategies
              </span>
            </div>
          </motion.div>

          {/* Action Center */}
          <div className="flex items-center gap-4">
            <NotificationCenter />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-2xl flex items-center gap-6">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="40" cy="40" r="36" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-800" />
                  <circle cx="40" cy="40" r="36" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="226" strokeDashoffset="45" className="text-brand-cyan" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-xl font-black text-white">82</div>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400 block mb-1">Strategic Maturity</span>
                <p className="text-sm font-bold text-brand-yellow">High Alignment</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-12">
        {/* Executive Metrics Bar */}
        <ExecutiveMetrics />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: AI & Actions */}
          <div className="lg:col-span-2 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <AiInsights />
              <StrategicActions />
            </div>

            {/* Pipeline Velocity & Savings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-xl border border-slate-100 dark:border-slate-800 group hover:border-brand-cyan transition-colors">
                <span className="text-slate-500 text-[10px] uppercase font-bold tracking-widest block mb-4">Pipeline Velocity</span>
                <div className="flex items-end justify-between">
                  <div className="flex items-end gap-3">
                    <span className="text-5xl font-heading font-black text-brand-dark dark:text-white">+24.8%</span>
                    <SafeIcon name="TrendingUp" className="w-8 h-8 text-brand-cyan mb-2" />
                  </div>
                  <div className="h-12 w-24 flex items-end gap-1 pb-2">
                    {[3, 5, 4, 7, 6, 8, 9].map((h, i) => (
                      <div key={i} className="flex-1 bg-brand-cyan/20 rounded-t-sm" style={{ height: `${h * 10}%` }}></div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-xl border border-slate-100 dark:border-slate-800 group hover:border-brand-yellow transition-colors">
                <span className="text-slate-500 text-[10px] uppercase font-bold tracking-widest block mb-4">Tech Overhead Reduction</span>
                <div className="flex items-end justify-between">
                  <div className="flex items-end gap-3">
                    <span className="text-5xl font-heading font-black text-brand-dark dark:text-white">$12.4k</span>
                    <SafeIcon name="Shield" className="w-8 h-8 text-brand-yellow mb-2" />
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-brand-yellow">Monthly Savings</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Pulse Link */}
            <Link to="/feedback-analytics" className="block p-8 bg-brand-cyan/5 border-2 border-slate-200 dark:border-slate-800 rounded-2xl group hover:border-brand-cyan transition-all relative overflow-hidden">
              <div className="absolute -right-8 -bottom-8 opacity-[0.03] group-hover:scale-110 transition-transform duration-700">
                <SafeIcon name="Activity" className="w-48 h-48" />
              </div>
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-6">
                  <div className="p-4 bg-brand-dark dark:bg-brand-cyan rounded-xl text-white dark:text-brand-dark shadow-lg">
                    <SafeIcon name="BarChart2" className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-brand-dark dark:text-white mb-1">Interactive Pulse Analytics</h3>
                    <p className="text-slate-600 dark:text-slate-400 font-medium">Real-time benchmark data from 1,200+ marketing executives.</p>
                  </div>
                </div>
                <div className="p-3 rounded-full border border-slate-200 dark:border-slate-700 group-hover:bg-brand-cyan group-hover:text-brand-dark transition-all">
                  <SafeIcon name="ArrowRight" className="w-6 h-6" />
                </div>
              </div>
            </Link>
          </div>

          {/* Right Column: Calendar, Reports & Filter */}
          <div className="space-y-8">
            <IntegratedCalendar />
            <QuarterlyReportCard />
            <ResourceFilterMini />
            
            {/* Consultation CTA */}
            <div className="bg-brand-red rounded-2xl p-8 shadow-xl text-white relative overflow-hidden group">
              <div className="absolute -right-8 -top-8 opacity-10 group-hover:scale-110 transition-transform duration-500">
                <SafeIcon name="Award" className="w-40 h-40" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-heading font-bold mb-2">Strategy Consultation</h3>
                <p className="text-white/80 text-sm font-medium mb-8 leading-relaxed">
                  Book a direct briefing to review your operational audit and explore expansion opportunities.
                </p>
                <a 
                  href="https://calendar.app.google/fEc5DqiofeUeHyRG6"
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-3 bg-white text-brand-red px-8 py-4 rounded-xl text-xs uppercase tracking-widest font-black hover:bg-slate-100 transition-all shadow-xl"
                >
                  Schedule Session <SafeIcon name="Calendar" className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;